import { useCallback, useState, type FormEvent } from "react";
import { Cable, CheckCircle2, Clock3, Copy, KeyRound, PauseCircle, Plus, RotateCcw, Send, ShieldCheck, SkipForward, Webhook } from "lucide-react";
import { platformConnectionCatalog, webhookEventCatalog, type CredentialMetadata, type PlatformApplicationConfigMetadata, type SessionView, type Tenant } from "@control-plane/contracts";
import { PageHeader } from "../components/PageHeader";
import { RemotePanel } from "../components/RemotePanel";
import { StatusBadge } from "../components/StatusBadge";
import { V3Select } from "../components/V3Select";
import { InlineFeedback } from "../components/Feedback";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Wizard } from "../components/Wizard";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { businessLabel, statusText } from "../lib/displayNames";
import { AiRiskCheck } from "../components/AssistantInlineHelp";
import { integrationsCapabilities } from "../lib/pageCapabilities";
import { V3PageTabs, type V3PageTab } from "../components/V3PageTabs";

type IntegrationsTab = "overview" | "delivery" | "configuration";

const integrationTabs: V3PageTab[] = [
  { value: "overview", label: "连接概览", icon: Cable, panelId: "integrations-overview-panel" },
  { value: "delivery", label: "投递管理", icon: Send, panelId: "integrations-delivery-panel" },
  { value: "configuration", label: "配置记录", icon: Webhook, panelId: "integrations-configuration-panel" },
];

type PlatformCatalogItem = (typeof platformConnectionCatalog)[number];
type ConnectionKind = PlatformCatalogItem["kinds"][number];
type ProbeStatus = "success" | "failed" | "unsupported";
type LatestProbe = {
  status: ProbeStatus | string;
  latencyMs: number | null;
  errorCode: string | null;
  evidence: Record<string, unknown> | null;
  startedAt: string;
  finishedAt: string;
};
type Connection = {
  id: string;
  tenantId?: string | null;
  tenantName?: string | null;
  tenantStatus?: Tenant["status"] | null;
  name: string;
  provider: string;
  kind: string;
  status: string;
  latestProbe?: LatestProbe | null;
};
type Endpoint = {
  id: string;
  url: string;
  events: string[];
  status: string;
  signing?: { activeVersion?: number | string | null; fingerprint?: string | null } | null;
};
type DeliveryTarget = { name?: string | null; url?: string | null; endpointUrl?: string | null; status?: string | null };
type OperatorResolution = { action?: string | null; reason?: string | null; actorEmail?: string | null; resolvedAt?: string | null } | null;
type Outbox = {
  id: string;
  eventType: string;
  aggregateType: string;
  attempts: number;
  status: string;
  tenantName?: string | null;
  targets?: DeliveryTarget[] | null;
  lastResult?: string | null;
  nextRetryAt?: string | null;
  operatorResolution?: OperatorResolution;
  canRetry?: boolean;
  canResolve?: boolean;
};
type GeneratedWebhookSecret = { value: string; generatedAt: string };
type ProbeConnectionResponse = { connection: Connection; probeRun: LatestProbe; idempotentReplay?: boolean };
type OutboxOperation = "retry" | "skip" | "terminate";
// 按权限条件加载：缺 credential.read_metadata 时不请求连接接口，整页仍可读。
type Data = { connections: Connection[] | null; webhooks: Endpoint[]; outbox: Outbox[]; tenants: Tenant[]; credentials: CredentialMetadata[]; platformConfigs: PlatformApplicationConfigMetadata[] };

const platformByProvider = Object.fromEntries(platformConnectionCatalog.map((item) => [item.provider, item])) as Record<string, PlatformCatalogItem | undefined>;
const providerLabels = { ...Object.fromEntries(platformConnectionCatalog.map((item) => [item.provider, item.label])), wechat: "微信" };
const platformForConnection = (provider: string) => platformByProvider[provider] ?? (provider === "wechat" ? platformByProvider.wechat_shop : undefined);
const connectionNameLabels: Record<string, string> = { "platform-application": "平台应用连接", "primary-connection": "主连接" };
const eventTypeLabels: Record<string, string> = Object.fromEntries(webhookEventCatalog.map((item) => [item.value, item.label]));
const aggregateTypeLabels: Record<string, string> = { audit_event: "审计记录", outbox_event: "投递事件", credential: "凭据", system_default: "系统默认配置" };
const kindLabels: Record<string, string> = { oauth: "官方授权", api_key: "密钥授权", webhook: "事件接收", service: "服务连接" };
const probeStatusLabels: Record<string, string> = { success: "探测通过", failed: "探测失败", unsupported: "尚未接入" };
const probeFailureLabels: Record<string, string> = {
  CALLBACK_NOT_READY: "回调地址还没有准备好",
  ADAPTER_MISSING: "平台探测能力尚未接入",
  CREDENTIAL_MISSING: "连接凭据还没有配置完整",
  UNAUTHORIZED: "授权信息未通过校验",
  TIMEOUT: "平台响应超时",
  CONFIG_INCOMPLETE: "配置还没有补齐",
  PROBE_UNSUPPORTED: "官方安全探测暂未接入",
  REMOTE_401: "授权未通过官方校验",
  REMOTE_403: "官方权限或白名单不满足",
  REMOTE_429: "官方平台限流",
  REMOTE_5XX: "官方平台暂时异常",
  REMOTE_TIMEOUT: "官方平台响应超时",
  NETWORK_ERROR: "网络连接失败",
  REMOTE_RESPONSE_UNVERIFIED: "官方返回缺少可核验证据",
  CONNECTION_PROBE_EVIDENCE_INCOMPLETE: "验证证据不完整",
  CONNECTION_CHANGED_DURING_PROBE: "验证期间配置已变化",
  PROBE_PLATFORM_APPLICATION_MISSING: "平台应用配置缺失",
  PROBE_PLATFORM_APPLICATION_UNAVAILABLE: "平台应用配置已停用",
  PROBE_PLATFORM_APPLICATION_VERSION_MISSING: "平台应用密钥版本缺失",
  PROBE_PLATFORM_APPLICATION_EXPIRED: "平台应用密钥已过期",
  PROBE_PLATFORM_APPLICATION_SECRET_INVALID: "平台应用密钥格式不正确",
  PROBE_PLATFORM_APPLICATION_DECRYPT_FAILED: "平台应用密钥无法解密",
  PROBE_TENANT_CREDENTIAL_NOT_FOUND: "店铺授权凭据不存在",
  PROBE_TENANT_CREDENTIAL_UNAVAILABLE: "店铺授权凭据已停用",
  PROBE_TENANT_CREDENTIAL_EXPIRED: "店铺授权凭据已过期",
  PROBE_TENANT_CREDENTIAL_VERSION_MISSING: "店铺授权版本缺失",
  PROBE_TENANT_CREDENTIAL_VERSION_RETIRED: "店铺授权版本已退役",
  PROBE_TENANT_CREDENTIAL_SECRET_INVALID: "店铺授权格式不正确",
  PROBE_TENANT_CREDENTIAL_DECRYPT_FAILED: "店铺授权无法解密",
};
const resolutionActionLabels: Record<string, string> = {
  retry: "重新投递",
  skip: "跳过投递",
  terminate: "终止重试",
};
const outboxOperationLabels: Record<OutboxOperation, { title: string; confirm: string; effect: string; tone: "primary" | "danger" }> = {
  retry: { title: "确认重新投递", confirm: "重新投递", effect: "系统会重新把这条业务事件送到接收方，并记录新的投递结果。", tone: "primary" },
  skip: { title: "确认跳过本次投递", confirm: "跳过投递", effect: "系统会停止处理这条待投递事件，后续需要通过人工记录说明原因。", tone: "danger" },
  terminate: { title: "确认终止重试", confirm: "终止重试", effect: "系统会停止后续自动重试，接收方可能不会再收到这条事件。", tone: "danger" },
};
const webhookSigningSecretBytes = 48;

const providerName = (value: string) => businessLabel(providerLabels, value, "平台待确认");
const connectionName = (value: string) => businessLabel(connectionNameLabels, value, "连接待确认");
const eventTypeName = (value: string) => businessLabel(eventTypeLabels, value, "事件待确认");
const aggregateTypeName = (value: string) => businessLabel(aggregateTypeLabels, value, "对象待确认");
const kindName = (value: string) => businessLabel(kindLabels, value, "授权方式待确认");
const probeStatusName = (value: string) => businessLabel(probeStatusLabels, value, "探测状态待确认");
const probeFailureName = (value: string) => businessLabel(probeFailureLabels, value, "原因待确认");
const resolutionActionName = (value: string) => businessLabel(resolutionActionLabels, value, "处置待确认");

export const providerSelectOptions: Array<{ value: string; label: string }> = platformConnectionCatalog.map((item) => ({ value: item.provider, label: item.label }));
export const webhookEventOptions = webhookEventCatalog.map(({ value, label }) => ({ value, label }));

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "暂无";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "时间待确认";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function createProbeKey(connectionId: string): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `probe-${connectionId}-${suffix}`;
}

function createActionKey(prefix: string): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${suffix}`;
}

export function generateWebhookSigningSecret(): string {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.getRandomValues) {
    throw new Error("当前浏览器不支持安全随机数，请更换现代浏览器后再操作。");
  }
  const bytes = new Uint8Array(webhookSigningSecretBytes);
  cryptoApi.getRandomValues(bytes);
  const secret = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `whsec_${secret}`;
}

function createGeneratedWebhookSecret(): GeneratedWebhookSecret {
  return { value: generateWebhookSigningSecret(), generatedAt: new Date().toISOString() };
}

function safeSecretFailureMessage(failure: unknown, fallback: string, secret?: string): string {
  const message = failure instanceof Error ? failure.message : fallback;
  if (!message.trim()) return fallback;
  if (secret && message.includes(secret)) return fallback;
  if (/signingSecret|whsec_/i.test(message)) return fallback;
  return message;
}

function probeTone(status: string | undefined): "success" | "danger" | "neutral" {
  if (status === "success") return "success";
  if (status === "failed" || status === "unsupported") return "danger";
  return "neutral";
}

function probeAdvice(probe: LatestProbe | null | undefined, actionError?: string): string {
  if (actionError) return "验证未完成，请稍后重试或检查平台应用配置。";
  if (!probe) return "先完成平台应用配置与密钥托管，再执行真实验证。";
  if (probe.status === "success") return "保留本次证据，按上线节奏定期复测。";
  if (probe.status === "unsupported") return "后端探测能力尚未接入，不能作为正式可用连接。";
  if (probe.status === "failed") return "按失败原因检查平台授权、回调地址和权限范围。";
  return "等待服务端返回可识别的探测结论。";
}

function evidenceSummary(evidence: Record<string, unknown> | null | undefined): string {
  if (!evidence || Object.keys(evidence).length === 0) return "暂无证据明细";
  const lines: string[] = [];
  if (typeof evidence.checkedAt === "string") lines.push(`检查时间：${formatDateTime(evidence.checkedAt)}`);
  if (typeof evidence.httpStatus === "number") lines.push(`官方响应：${evidence.httpStatus}`);
  if (typeof evidence.latencyMs === "number") lines.push(`响应耗时：${evidence.latencyMs} ms`);
  if (Array.isArray(evidence.missingCredentialFields)) lines.push("缺少配置：平台应用或店铺授权还未补齐");
  if (typeof evidence.summary === "string") lines.push(`证据摘要：${evidence.summary.slice(0, 80)}`);
  if (typeof evidence.nextAdvice === "string") lines.push(`下一步：${evidence.nextAdvice.slice(0, 80)}`);
  return lines.length ? lines.join("；") : "服务端已记录证据";
}

function webhookFingerprint(item: Endpoint): string {
  return item.signing?.fingerprint ?? "暂无指纹";
}

function webhookVersion(item: Endpoint): string {
  const version = item.signing?.activeVersion;
  return version === null || version === undefined || version === "" ? "暂无版本" : `第 ${version} 版`;
}

function targetSummary(targets: DeliveryTarget[] | null | undefined): string {
  if (!targets?.length) return "暂无目标";
  return targets.map((target) => {
    return target.name || target.url || target.endpointUrl || "目标待确认";
  }).slice(0, 3).join("、") + (targets.length > 3 ? ` 等 ${targets.length} 个目标` : "");
}

function lastResultSummary(result: string | null | undefined): string {
  return result ? result.slice(0, 120) : "暂无回执";
}

function operatorResolutionSummary(resolution: OperatorResolution | undefined): string {
  if (!resolution) return "暂无人工处置";
  if (typeof resolution === "string") return resolution || "暂无人工处置";
  const parts = [
    resolution.action ? resolutionActionName(resolution.action) : null,
    resolution.reason ? String(resolution.reason).slice(0, 80) : null,
    resolution.actorEmail ? `处理人 ${resolution.actorEmail}` : null,
    resolution.resolvedAt ? formatDateTime(resolution.resolvedAt) : null,
  ].filter(Boolean);
  return parts.length ? parts.join("；") : "已记录人工处置";
}

function canOperateOutbox(item: Outbox): boolean {
  const hasServerCapabilities = item.canRetry !== undefined || item.canResolve !== undefined;
  const allowed = hasServerCapabilities ? Boolean(item.canRetry || item.canResolve) : true;
  return allowed && !item.operatorResolution && !["delivered", "skipped", "terminated", "succeeded", "completed"].includes(item.status);
}

function canProbeConnection(item: Connection): boolean {
  return Boolean(platformForConnection(item.provider)?.canProbe) && (!item.tenantStatus || item.tenantStatus === "active");
}

function PlatformLogo({ platform, size = "md" }: { platform: PlatformCatalogItem; size?: "sm" | "md" | "lg" }) {
  return <img className={`platform-logo platform-logo-${size}`} src={platform.logoPath} alt={platform.label} loading="lazy" />;
}

export function IntegrationsPage({ session }: { session: SessionView }) {
  const [activeTab, setActiveTab] = useState<IntegrationsTab>("overview");
  const caps = integrationsCapabilities(session.actions);
  const loader = useCallback(async (): Promise<Data> => {
    const canLoadConnectionChoices = caps.showCreateConnection && session.actions.includes("tenant.read") && session.actions.includes("credential.read_metadata");
    const [connections, webhooks, outbox, tenants, credentials, platformConfigs] = await Promise.all([
      caps.loadConnections ? api<{ items: Connection[] }>("/api/connections") : Promise.resolve(null),
      caps.loadWebhooks ? api<{ items: Endpoint[] }>("/api/webhooks") : Promise.resolve({ items: [] as Endpoint[] }),
      caps.loadOutbox ? api<{ items: Outbox[] }>("/api/outbox") : Promise.resolve({ items: [] as Outbox[] }),
      canLoadConnectionChoices ? api<{ items: Tenant[] }>("/api/tenants") : Promise.resolve({ items: [] as Tenant[] }),
      canLoadConnectionChoices ? api<{ items: CredentialMetadata[] }>("/api/credentials?scope=tenant") : Promise.resolve({ items: [] as CredentialMetadata[] }),
      session.actions.includes("credential.read_metadata") ? api<{ items: PlatformApplicationConfigMetadata[] }>("/api/platform-app-configs") : Promise.resolve({ items: [] as PlatformApplicationConfigMetadata[] }),
    ]);
    return { connections: connections?.items ?? null, webhooks: webhooks.items, outbox: outbox.items, tenants: tenants.items, credentials: credentials.items, platformConfigs: platformConfigs.items };
  }, [caps.loadConnections, caps.loadWebhooks, caps.loadOutbox, caps.showCreateConnection, session.actions]);
  const remote = useRemote(loader);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [probeBusy, setProbeBusy] = useState<Record<string, boolean>>({});
  const [probeErrors, setProbeErrors] = useState<Record<string, string>>({});
  const [localProbes, setLocalProbes] = useState<Record<string, LatestProbe>>({});
  const [rotateTarget, setRotateTarget] = useState<Endpoint | null>(null);
  const [rotateReason, setRotateReason] = useState("");
  const [rotateSecret, setRotateSecret] = useState<GeneratedWebhookSecret | null>(null);
  const [rotateSecretSaved, setRotateSecretSaved] = useState(false);
  const [rotateBusy, setRotateBusy] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [pendingOutbox, setPendingOutbox] = useState<{ item: Outbox; operation: OutboxOperation } | null>(null);
  const [outboxReason, setOutboxReason] = useState("");
  const [outboxBusy, setOutboxBusy] = useState(false);
  const [outboxError, setOutboxError] = useState<string | null>(null);

  function closeRotateDialog() {
    setRotateTarget(null);
    setRotateReason("");
    setRotateSecret(null);
    setRotateSecretSaved(false);
    setRotateError(null);
  }

  function prepareRotateSecret() {
    try {
      setRotateSecret(createGeneratedWebhookSecret());
      setRotateSecretSaved(false);
      setRotateError(null);
    } catch (failure) {
      setRotateError(safeSecretFailureMessage(failure, "当前浏览器不支持安全随机数，请更换现代浏览器后再轮换。"));
    }
  }

  async function rotateSigningKey() {
    if (!rotateTarget) return;
    if (!rotateReason.trim()) { setRotateError("请填写轮换原因。"); return; }
    if (!rotateSecret) { setRotateError("请先生成新的签名密钥。"); return; }
    if (!rotateSecretSaved) { setRotateError("请先确认密钥已经保存到接收系统或安全凭据库。"); return; }
    const submittedSecret = rotateSecret.value;
    setRotateBusy(true);
    setRotateError(null);
    try {
      await api(`/api/webhooks/${rotateTarget.id}/signing-secret/rotate`, {
        method: "POST",
        body: JSON.stringify({ reason: rotateReason.trim(), signingSecret: submittedSecret, idempotencyKey: createActionKey("webhook-rotate") }),
      });
      setRotateSecret(null);
      setRotateSecretSaved(false);
      setRotateTarget(null);
      setRotateReason("");
      await remote.reload();
    } catch (failure) {
      setRotateSecret(null);
      setRotateSecretSaved(false);
      setRotateError(safeSecretFailureMessage(failure, "轮换失败，请稍后重试。为保护密钥，页面已清空本次生成值，请重新生成后再提交。", submittedSecret));
    } finally {
      setRotateBusy(false);
    }
  }

  function openOutboxOperation(item: Outbox, operation: OutboxOperation) {
    setPendingOutbox({ item, operation });
    setOutboxReason("");
    setOutboxError(null);
  }

  async function submitOutboxOperation() {
    if (!pendingOutbox) return;
    if (!outboxReason.trim()) { setOutboxError("请填写处理原因。"); return; }
    setOutboxBusy(true);
    setOutboxError(null);
    try {
      await api(`/api/outbox/${pendingOutbox.item.id}/${pendingOutbox.operation}`, {
        method: "POST",
        body: JSON.stringify({ reason: outboxReason.trim(), idempotencyKey: createActionKey(`outbox-${pendingOutbox.operation}`) }),
      });
      setPendingOutbox(null);
      setOutboxReason("");
      await remote.reload();
    } catch (failure) {
      setOutboxError(failure instanceof Error ? failure.message : "操作失败，请稍后重试");
    } finally {
      setOutboxBusy(false);
    }
  }

  async function probeConnection(item: Connection) {
    setProbeBusy((current) => ({ ...current, [item.id]: true }));
    setProbeErrors((current) => {
      const next = { ...current };
      delete next[item.id];
      return next;
    });
    try {
      const response = await api<ProbeConnectionResponse>(`/api/connections/${item.id}/probe`, {
        method: "POST",
        body: JSON.stringify({ idempotencyKey: createProbeKey(item.id) }),
      });
      setLocalProbes((current) => ({ ...current, [item.id]: response.probeRun }));
      await remote.reload();
    } catch (failure) {
      setProbeErrors((current) => ({ ...current, [item.id]: failure instanceof Error ? failure.message : "验证失败，请稍后重试" }));
    } finally {
      setProbeBusy((current) => ({ ...current, [item.id]: false }));
    }
  }

  const connectionColumns: Array<DataTableColumn<Connection>> = [
    {
      key: "connection",
      header: "平台连接",
      width: "28%",
      render: (item) => {
        const platform = platformByProvider[item.provider];
        return <span className="integration-entity">
          {platform ? <PlatformLogo platform={platform} /> : <span className="integration-entity-fallback"><Cable size={16} /></span>}
          <span><strong>{connectionName(item.name)}</strong><small>{item.tenantName || "企业名称需权限"} · {providerName(item.provider)} · {kindName(item.kind)}</small></span>
        </span>;
      },
    },
    {
      key: "status",
      header: "连接状态",
      render: (item) => {
        const latestProbe = localProbes[item.id] ?? item.latestProbe;
        if (item.status === "revoked") return <StatusBadge value={item.status} />;
        if (latestProbe?.status === "success") return <span className="probe-pill success"><i />探测通过</span>;
        if (latestProbe?.status === "failed") return <span className="probe-pill danger"><i />探测失败</span>;
        if (latestProbe?.status === "unsupported") return <span className="probe-pill danger"><i />待接入探测</span>;
        return <span className="probe-pill neutral"><i />待真实验证</span>;
      },
    },
    {
      key: "probe",
      header: "最近探测",
      width: "24%",
      render: (item) => {
        const latestProbe = localProbes[item.id] ?? item.latestProbe;
        const error = probeErrors[item.id];
        return <span className="probe-cell">
          <span className={`probe-pill ${probeTone(latestProbe?.status)}`}><i />{latestProbe ? probeStatusName(latestProbe.status) : "未验证"}</span>
          <small>开始：{formatDateTime(latestProbe?.startedAt)}</small>
          <small>完成：{formatDateTime(latestProbe?.finishedAt)}</small>
          {latestProbe?.latencyMs !== null && latestProbe?.latencyMs !== undefined && <small>耗时：<span className="app-number">{latestProbe.latencyMs}</span> ms</small>}
          {latestProbe?.errorCode && <small>失败原因：{probeFailureName(latestProbe.errorCode)}</small>}
          {error && <small className="danger-text">验证未完成</small>}
        </span>;
      },
    },
    {
      key: "evidence",
      header: "证据与建议",
      render: (item) => {
        const latestProbe = localProbes[item.id] ?? item.latestProbe;
        return <span className="probe-evidence">
          <small>{evidenceSummary(latestProbe?.evidence)}</small>
          <strong>{item.tenantStatus && item.tenantStatus !== "active" ? "企业可先准备连接，待正式启用后再验证。" : probeAdvice(latestProbe, probeErrors[item.id])}</strong>
        </span>;
      },
    },
  ];

  const webhookColumns: Array<DataTableColumn<Endpoint>> = [
    { key: "url", header: "接收地址", render: (item) => <span className="integration-url">{item.url}</span> },
    { key: "events", header: "接收事件", render: (item) => <span className="event-list">{item.events.map((event) => eventTypeName(event)).join("、")}</span> },
    { key: "signing", header: "签名密钥", width: "190px", render: (item) => <span className="probe-cell"><strong>{webhookVersion(item)}</strong><small>指纹：{webhookFingerprint(item)}</small></span> },
    { key: "status", header: "状态", width: "120px", render: (item) => <StatusBadge value={item.status} /> },
  ];
  const outboxColumns: Array<DataTableColumn<Outbox>> = [
    { key: "event", header: "投递事件", render: (item) => <span className="probe-cell"><strong>{eventTypeName(item.eventType)}</strong><small>业务对象：{aggregateTypeName(item.aggregateType)}</small></span> },
    { key: "targets", header: "投递目标", render: (item) => <span className="integration-url">{targetSummary(item.targets)}</span> },
    { key: "attempts", header: "尝试次数", width: "110px", render: (item) => <span className="app-number">{item.attempts}</span> },
    { key: "result", header: "最后结果", render: (item) => <span className="probe-cell"><span>{lastResultSummary(item.lastResult)}</span><small>下次重试：{formatDateTime(item.nextRetryAt)}</small><small>{operatorResolutionSummary(item.operatorResolution)}</small></span> },
    { key: "tenant", header: "影响企业", width: "150px", render: (item) => <span className="integration-url">{item.tenantName || "平台范围"}</span> },
    { key: "status", header: "状态", width: "130px", render: (item) => <StatusBadge value={item.status} /> },
  ];

  const outboxRowActions = (item: Outbox) => caps.showRetryOutbox && canOperateOutbox(item) ? <div className="row-action-group">
    {(item.canRetry ?? true) && <button className="button secondary compact" type="button" onClick={() => openOutboxOperation(item, "retry")}><RotateCcw size={14} />重试</button>}
    {item.canResolve !== false && <button className="button secondary compact" type="button" onClick={() => openOutboxOperation(item, "skip")}><SkipForward size={14} />跳过</button>}
    {item.canResolve !== false && <button className="button ghost danger compact" type="button" onClick={() => openOutboxOperation(item, "terminate")}><PauseCircle size={14} />终止</button>}
    <AiRiskCheck action="重试事件投递" detail={`事件 ${eventTypeName(item.eventType)}，当前状态 ${statusText(item.status)}，已尝试 ${item.attempts} 次。重试前要确认接收地址和失败原因。`} />
  </div> : null;

  return <div className="page integrations-page">
    <PageHeader
      icon={Cable}
      title="连接与可靠投递"
      meta={<span>管理平台连接、接收地址与事件投递</span>}
      navigation={<V3PageTabs tabs={integrationTabs} active={activeTab} onChange={(value) => setActiveTab(value as IntegrationsTab)} ariaLabel="连接与可靠投递页内视图" />}
    />
    <RemotePanel state={remote.state} onRetry={remote.reload}>{(data) => {
      return <div className="workspace-stack integrations-tab-content">
      {activeTab === "overview" && <div id="integrations-overview-panel" role="tabpanel" aria-labelledby="v3-page-tab-overview" className="workspace-stack">
        {data.connections !== null && <SectionCard
        icon={Cable}
        title="平台连接"
        meta={<><span className="app-number">{data.connections.length}</span> 条</>}
        variant="ledger"
        action={<div className="inline-actions">{caps.showCreateConnection && <button className="button primary" onClick={() => setShowConnectionForm(true)}><Plus size={16} />新增连接</button>}{caps.showCreateConnection && <AiRiskCheck action="新增平台连接" detail="平台连接会影响外部平台对接和事件投递，保存前要确认平台、授权方式和回调路径。" />}</div>}
      >
        <DataTable
          ariaLabel="平台连接台账"
          columns={connectionColumns}
          rows={data.connections}
          rowKey={(item) => item.id}
          empty="暂无平台连接，请先从平台目录选择要对接的官方平台。"
          rowActions={(item) => caps.showVerifyConnection && canProbeConnection(item) ? <div className="row-action-group">
            <button className="button secondary" disabled={probeBusy[item.id] || item.status === "revoked"} onClick={() => void probeConnection(item)}>{probeBusy[item.id] ? "验证中…" : "验证连接"}</button>
            <AiRiskCheck action="验证平台连接" detail={`验证会由服务端读取托管配置并记录探测证据，当前连接状态为 ${statusText(item.status)}。`} />
          </div> : null}
        />
        </SectionCard>}
        <section className="catalog-strip" aria-label="平台目录">
          {platformConnectionCatalog.map((platform) => <article key={platform.provider}>
            <PlatformLogo platform={platform} size="sm" />
            <strong>{platform.label}</strong>
            <span>{platform.canProbe ? "支持真实探测" : "暂不支持探测"}</span>
          </article>)}
        </section>
      </div>}
      {activeTab === "delivery" && <div id="integrations-delivery-panel" role="tabpanel" aria-labelledby="v3-page-tab-delivery">
        <SectionCard icon={Send} title="事件投递台账" meta={<><span className="app-number">{data.outbox.length}</span> 条</>} variant="ledger">
          <DataTable ariaLabel="事件投递队列" columns={outboxColumns} rows={data.outbox} rowKey={(item) => item.id} empty="暂无待投递事件。" rowActions={outboxRowActions} />
        </SectionCard>
      </div>}
      {activeTab === "configuration" && <div id="integrations-configuration-panel" role="tabpanel" aria-labelledby="v3-page-tab-configuration">
        <SectionCard
          icon={Webhook}
          title="事件接收地址"
          meta={<><span className="app-number">{data.webhooks.length}</span> 条</>}
          variant="ledger"
          action={<div className="inline-actions">{caps.showCreateWebhook && <button className="button secondary" onClick={() => setShowWebhookForm(true)}><Webhook size={16} />新增地址</button>}{caps.showCreateWebhook && <AiRiskCheck action="新增事件接收地址" detail="事件接收地址会收到平台审计或投递事件，生产环境必须使用可信 HTTPS 地址。" />}</div>}
        >
          <DataTable
            ariaLabel="事件接收地址台账"
            columns={webhookColumns}
            rows={data.webhooks}
            rowKey={(item) => item.id}
            empty="暂无接收地址。"
            rowActions={(item) => caps.showCreateWebhook ? <button className="button secondary" type="button" onClick={() => { setRotateTarget(item); setRotateReason(""); setRotateError(null); }}><RotateCcw size={14} />轮换签名</button> : null}
          />
        </SectionCard>
      </div>}
    </div>;
    }}</RemotePanel>
    {showConnectionForm && remote.state.status === "ready" && <ConnectionCreateForm tenants={remote.state.data.tenants} credentials={remote.state.data.credentials} platformConfigs={remote.state.data.platformConfigs} onClose={() => setShowConnectionForm(false)} onDone={() => { setShowConnectionForm(false); void remote.reload(); }} />}
    {showWebhookForm && <WebhookCreateForm
      onClose={() => setShowWebhookForm(false)}
      onCreated={() => { setShowWebhookForm(false); void remote.reload(); }}
    />}
    {rotateTarget && <ConfirmDialog
      title="确认轮换签名密钥"
      description="轮换前由浏览器生成新的签名密钥，提交成功后页面会立即清空明文。"
      confirmLabel="确认已保存并轮换"
      tone="danger"
      busy={rotateBusy}
      confirmDisabled={!rotateSecret || !rotateSecretSaved}
      onClose={() => { if (!rotateBusy) closeRotateDialog(); }}
      onConfirm={() => { void rotateSigningKey(); }}
    >
      <div className="confirm-form">
        <p>当前接收地址：{rotateTarget.url}</p>
        <p>当前签名：{webhookVersion(rotateTarget)} · 指纹：{webhookFingerprint(rotateTarget)}</p>
        <label>轮换原因<textarea value={rotateReason} onChange={(event) => setRotateReason(event.target.value)} placeholder="例如：接收方完成季度密钥轮换" /></label>
        <WebhookSecretSafetyStep
          secret={rotateSecret}
          confirmed={rotateSecretSaved}
          onGenerate={prepareRotateSecret}
          onConfirmedChange={setRotateSecretSaved}
        />
        {rotateError && <InlineFeedback tone="danger">{rotateError}</InlineFeedback>}
      </div>
    </ConfirmDialog>}
    {pendingOutbox && <ConfirmDialog
      title={outboxOperationLabels[pendingOutbox.operation].title}
      description={outboxOperationLabels[pendingOutbox.operation].effect}
      confirmLabel={outboxOperationLabels[pendingOutbox.operation].confirm}
      tone={outboxOperationLabels[pendingOutbox.operation].tone}
      busy={outboxBusy}
      onClose={() => { if (!outboxBusy) setPendingOutbox(null); }}
      onConfirm={() => { void submitOutboxOperation(); }}
    >
      <div className="confirm-form">
        <p>业务事件：{eventTypeName(pendingOutbox.item.eventType)}，影响企业：{pendingOutbox.item.tenantName || "平台范围"}。</p>
        <p>当前状态：{statusText(pendingOutbox.item.status)}，已尝试 <span className="app-number">{pendingOutbox.item.attempts}</span> 次。</p>
        <label>处理原因<textarea value={outboxReason} onChange={(event) => setOutboxReason(event.target.value)} placeholder="请写明本次处理依据，便于后续审计复核" /></label>
        {outboxError && <InlineFeedback tone="danger">{outboxError}</InlineFeedback>}
      </div>
    </ConfirmDialog>}
  </div>;
}

function ConnectionGuide({ platform, kind, platformConfig }: { platform: PlatformCatalogItem; kind: string; platformConfig: PlatformApplicationConfigMetadata | undefined }) {
  const callbackUrl = platformConfig?.callbackUrl ?? "";
  const scopeLabels = (platform.scopeOptions ?? []).map((item) => item.label).join("、");
  const authorizationGuide = platform.authorizationGuide ?? "按平台官方后台要求完成应用配置，再在凭据保险库保存店铺授权。";
  const testSteps = platform.testSteps ?? ["保存连接后执行真实探测"];
  function copyCallbackUrl() {
    if (callbackUrl) void navigator.clipboard?.writeText(callbackUrl);
  }
  return <div className="connection-guide">
    <div className="guide-hero">
      <PlatformLogo platform={platform} size="lg" />
      <div><strong>{platform.label}</strong><span>{kindName(kind)} · {platform.canSubscribeEvents ? "可订阅平台事件" : "事件订阅待接入"}</span></div>
    </div>
    <div className="guide-grid">
      <span><ShieldCheck size={16} /><strong>授权说明</strong><small>{authorizationGuide}</small></span>
      <span><Cable size={16} /><strong>官方回调地址</strong>{callbackUrl ? <small className="callback-copy"><em>{callbackUrl}</em><button type="button" className="button secondary compact" onClick={copyCallbackUrl}><Copy size={13} />复制</button></small> : <small>请先到平台应用密钥页保存该平台的官方回调地址。</small>}</span>
      <span><KeyRound size={16} /><strong>连接用途</strong><small>{scopeLabels || "当前平台暂未开放可选用途。"}</small></span>
      <span><CheckCircle2 size={16} /><strong>测试步骤</strong><small>{testSteps.join("；")}</small></span>
    </div>
  </div>;
}

export function ConnectionCreateForm({ tenants = [], credentials = [], platformConfigs = [], onClose, onDone }: { tenants?: Tenant[]; credentials?: CredentialMetadata[]; platformConfigs?: PlatformApplicationConfigMetadata[]; onClose: () => void; onDone: () => void }) {
  const first = platformConnectionCatalog[0];
  const [provider, setProvider] = useState<string>(first.provider);
  const [kind, setKind] = useState<ConnectionKind>(first.kinds[0]);
  const [tenantId, setTenantId] = useState("");
  const [credentialId, setCredentialId] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [callbackConfirmed, setCallbackConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const platform = platformByProvider[provider] ?? first;
  const platformConfig = platformConfigs.find((item) => item.provider === provider);
  const callbackUrl = platformConfig?.callbackUrl ?? "";
  const kindOptions = platform.kinds.map((value) => ({ value, label: kindName(value) }));
  const selectableTenants = tenants.filter((tenant) => tenant.status !== "archived" && tenant.status !== "purged");
  const selectedTenant = selectableTenants.find((tenant) => tenant.id === tenantId);
  const tenantOptions = selectableTenants.map((tenant) => ({ value: tenant.id, label: `${tenant.name} · ${statusText(tenant.status)}` }));
  const scopeOptions = (platform.scopeOptions ?? []).map((item) => ({ value: item.value, label: item.label }));
  const usableCredentials = credentials.filter((credential) =>
    credential.scope === "tenant" &&
    credential.status === "active" &&
    credential.tenantId === tenantId &&
    credential.provider === provider &&
    (!credential.expiresAt || new Date(credential.expiresAt).getTime() > Date.now())
  );
  const credentialOptions = usableCredentials.map((credential) => ({
    value: credential.id,
    label: `${providerName(credential.provider)}店铺授权 · 第 ${credential.activeVersion} 版 · 指纹 ${credential.fingerprint}`,
  }));

  function choosePlatform(item: PlatformCatalogItem) {
    setProvider(item.provider);
    setKind(item.kinds[0]);
    setCredentialId("");
    setScopes([]);
  }

  function toggleScope(value: string) {
    setScopes((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  }

  async function submit() {
    if (!name.trim()) { setError("请填写连接名称。"); return; }
    if (!tenantId) { setError("请选择要接入的企业。"); return; }
    if (!credentialId) { setError("请选择该企业在保险库中的店铺授权凭据。"); return; }
    if (!scopes.length) { setError("请至少选择一个连接用途。"); return; }
    if (!callbackUrl) { setError("请先到平台应用密钥页保存该平台的官方回调地址。"); return; }
    if (!callbackConfirmed) { setError("请确认官方后台已配置页面显示的完整回调地址。"); return; }
    if (reason.trim().length < 6) { setError("请填写本次接入原因，至少 6 个字。"); return; }
    setBusy(true);
    setError(null);
    try {
      await api("/api/connections", {
        method: "POST",
        body: JSON.stringify({
          tenantId,
          provider,
          kind,
          name: name.trim(),
          credentialId,
          scopes,
          callbackConfirmed: true,
          reason: reason.trim(),
          idempotencyKey: createActionKey("connection-create"),
        }),
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "创建失败，请重试");
    } finally {
      setBusy(false);
    }
  }

  return <Wizard
    title="新增平台连接"
    description="先从正式平台目录选择对象，再登记连接名称；密钥和值班凭证仍由保险库统一托管。"
    onClose={onClose}
    submitLabel="创建连接"
    submitting={busy}
    submitError={error}
    onSubmit={() => { void submit(); }}
    steps={[
      {
        key: "platform",
        title: "选择平台",
        summary: "平台目录来自正式平台连接目录，只显示官方 logo 和中文名。",
        content: <div className="platform-picker">
          {platformConnectionCatalog.map((item) => <button key={item.provider} type="button" className={item.provider === provider ? "selected" : ""} onClick={() => choosePlatform(item)} aria-pressed={item.provider === provider}>
            <PlatformLogo platform={item} />
            <span><strong>{item.label}</strong><small>{item.canProbe ? "可执行真实探测" : "探测能力待接入"}</small></span>
          </button>)}
        </div>,
      },
      {
        key: "details",
        title: "登记授权",
        validate: () => {
          if (!tenantId) return "请选择要接入的企业。";
          if (!name.trim()) return "请填写连接名称。";
          if (!credentialId) return "请选择该企业在保险库中的店铺授权凭据。";
          if (!scopes.length) return "请至少选择一个连接用途。";
          return null;
        },
        content: <form className="form-grid" onSubmit={(event: FormEvent) => event.preventDefault()}>
          <label className="full">接入企业{tenantOptions.length ? <V3Select value={tenantId} onChange={(value) => { setTenantId(value); setCredentialId(""); }} ariaLabel="接入企业" options={tenantOptions} /> : <span className="form-hint">暂无可准备连接的企业，请先完成企业开通或恢复服务。</span>}</label>
          {selectedTenant && selectedTenant.status !== "active" && <InlineFeedback tone="warning">该企业可以先保存连接准备材料，正式验证需要等企业启用后再执行。</InlineFeedback>}
          <label className="full">平台名称<V3Select value={provider} onChange={(value) => choosePlatform(platformByProvider[value] ?? first)} ariaLabel="平台名称" options={providerSelectOptions} /></label>
          <label>授权方式<V3Select value={kind} onChange={(value) => setKind(value as ConnectionKind)} ariaLabel="授权方式" options={kindOptions} /></label>
          <label>连接名称<input value={name} onChange={(event) => setName(event.target.value)} placeholder="如：生产主连接" required /><small className="field-hint">用于区分同一平台的多条连接，建议使用中文业务名。</small></label>
          <label className="full">店铺授权凭据{credentialOptions.length ? <V3Select value={credentialId} onChange={setCredentialId} ariaLabel="店铺授权凭据" options={credentialOptions} /> : <span className="form-hint">没有匹配的可用店铺授权，请先到凭据保险库写入同企业、同平台的店铺授权。</span>}</label>
          <fieldset className="checkbox-group full">
            <legend>连接用途</legend>
            {scopeOptions.map((option) => <label key={option.value} className="checkbox-option"><input type="checkbox" checked={scopes.includes(option.value)} onChange={() => toggleScope(option.value)} />{option.label}</label>)}
          </fieldset>
          <div className="full"><ConnectionGuide platform={platform} kind={kind} platformConfig={platformConfig} /></div>
        </form>,
      },
      {
        key: "confirm",
        title: "确认边界",
        content: <div className="connection-confirm">
          <ConnectionGuide platform={platform} kind={kind} platformConfig={platformConfig} />
          <label className="checkbox-option"><input type="checkbox" disabled={!callbackUrl} checked={callbackConfirmed} onChange={(event) => setCallbackConfirmed(event.target.checked)} />我已确认官方后台使用页面显示的完整回调地址</label>
          <label className="full">接入原因<textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="例如：为杭州示例企业接入抖音主店铺" /></label>
          <InlineFeedback tone="warning">当前只创建连接登记，不代表官方授权完成；勾选仅代表操作者完成核对，验证结果必须来自服务端探测。</InlineFeedback>
        </div>,
      },
    ]}
  />;
}

function WebhookSecretSafetyStep({ secret, confirmed, onGenerate, onConfirmedChange }: {
  secret: GeneratedWebhookSecret | null;
  confirmed: boolean;
  onGenerate: () => void;
  onConfirmedChange: (confirmed: boolean) => void;
}) {
  const [copyFeedback, setCopyFeedback] = useState<"success" | "danger" | null>(null);
  async function copySecret() {
    if (!secret) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("CLIPBOARD_UNAVAILABLE");
      await navigator.clipboard.writeText(secret.value);
      setCopyFeedback("success");
    } catch {
      setCopyFeedback("danger");
    }
  }
  return <div className="one-time-secret">
    <InlineFeedback tone="warning">签名密钥由当前浏览器安全生成，只在提交前展示一次；不要发送到聊天、截图或工单里。</InlineFeedback>
    <div className="inline-actions">
      <button type="button" className="button secondary" onClick={() => { setCopyFeedback(null); onGenerate(); }}>
        <KeyRound size={16} />{secret ? "重新生成签名密钥" : "生成签名密钥"}
      </button>
      {secret && <button type="button" className="button secondary" onClick={() => { void copySecret(); }}><Copy size={16} />复制密钥</button>}
    </div>
    {secret
      ? <>
        <label>一次性签名密钥<textarea readOnly value={secret.value} onFocus={(event) => event.currentTarget.select()} /></label>
        <small className="field-hint">生成时间：{formatDateTime(secret.generatedAt)}。提交后页面会立即清空明文，服务端不会再返回这段密钥。</small>
      </>
      : <small className="field-hint">点击生成后，请先保存到接收系统或安全凭据库，再提交。</small>}
    <label className="checkbox-option">
      <input type="checkbox" disabled={!secret} checked={confirmed} onChange={(event) => onConfirmedChange(event.target.checked)} />
      我已把密钥保存到接收系统或安全凭据库
    </label>
      {copyFeedback === "success" && <InlineFeedback tone="success">密钥已复制，请立即保存到安全位置。</InlineFeedback>}
      {copyFeedback === "danger" && <InlineFeedback tone="danger">复制失败，请手动选中密钥并保存。</InlineFeedback>}
  </div>;
}

export function WebhookCreateForm({ onClose, onCreated, onDone }: { onClose: () => void; onCreated?: () => void; onDone?: () => void }) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>([]);
  const [signingSecret, setSigningSecret] = useState<GeneratedWebhookSecret | null>(null);
  const [secretSaved, setSecretSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  function close() {
    setSigningSecret(null);
    setSecretSaved(false);
    onClose();
  }

  function prepareSigningSecret() {
    try {
      setSigningSecret(createGeneratedWebhookSecret());
      setSecretSaved(false);
      setError("");
    } catch (failure) {
      setError(safeSecretFailureMessage(failure, "当前浏览器不支持安全随机数，请更换现代浏览器后再创建。"));
    }
  }

  function toggleEvent(value: string) {
    setEvents((current) => {
      if (current.includes(value)) return current.filter((item) => item !== value);
      if (value === "*") return ["*"];
      return [...current.filter((item) => item !== "*"), value];
    });
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!url.trim()) { setError("请填写事件接收地址。"); return; }
    if (!events.length) { setError("请至少勾选一个订阅事件。"); return; }
    if (!signingSecret) { setError("请先生成签名密钥。"); return; }
    if (!secretSaved) { setError("请先确认密钥已经保存到接收系统或安全凭据库。"); return; }
    const submittedSecret = signingSecret.value;
    setBusy(true);
    setError("");
    try {
      await api("/api/webhooks", { method: "POST", body: JSON.stringify({ tenantId: null, url: url.trim(), events, signingSecret: submittedSecret, secretCredentialId: null }) });
      setSigningSecret(null);
      setSecretSaved(false);
      onCreated?.();
      if (!onCreated) onDone?.();
    } catch (failure) {
      setSigningSecret(null);
      setSecretSaved(false);
      setError(safeSecretFailureMessage(failure, "创建失败，请稍后重试。为保护密钥，页面已清空本次生成值，请重新生成后再提交。", submittedSecret));
    } finally {
      setBusy(false);
    }
  }
  return <Wizard
    title="新增事件接收地址"
    description="平台会把勾选的事件发送到该地址。签名密钥由浏览器生成，只用于本次安全写入，服务端不会回传。"
    onClose={close}
    submitLabel="创建接收地址"
    submitting={busy}
    submitError={error || null}
    submitDisabled={!signingSecret || !secretSaved}
    onSubmit={() => {
      const synthetic = { preventDefault() {} } as FormEvent;
      void submit(synthetic);
    }}
    steps={[
      {
        key: "address",
        title: "接收地址",
        validate: () => url.trim() ? null : "请填写事件接收地址。",
        content: <form className="form-grid" onSubmit={submit}>
          <label className="full">事件接收地址<input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://example.com/platform-events" required /><small className="field-hint">必须以 https:// 开头；生产环境只能使用可信的 HTTPS 地址。</small></label>
        </form>,
      },
      {
        key: "events",
        title: "订阅事件",
        validate: () => events.length ? null : "请至少勾选一个订阅事件。",
        content: <fieldset className="checkbox-group">
          <legend>订阅事件</legend>
          {webhookEventOptions.map((option) => <label key={option.value} className="checkbox-option"><input type="checkbox" checked={events.includes(option.value)} onChange={() => toggleEvent(option.value)} />{option.label}</label>)}
          <small className="field-hint">从正式中文事件目录中勾选，可多选。</small>
        </fieldset>,
      },
      {
        key: "secret",
        title: "保存密钥",
        content: <div className="connection-confirm">
          <WebhookSecretSafetyStep
            secret={signingSecret}
            confirmed={secretSaved}
            onGenerate={prepareSigningSecret}
            onConfirmedChange={setSecretSaved}
          />
          <InlineFeedback tone="info">创建后只表示接收地址已登记；后续送达状态仍以投递队列和真实回执为准。</InlineFeedback>
          <div className="event-review"><Clock3 size={16} /><span>已选择 <strong className="app-number">{events.length}</strong> 类事件</span></div>
        </div>,
      },
    ]}
  />;
}
